import { useState, useEffect } from 'react';
import { Package, Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, Settings, AlertTriangle } from 'lucide-react';
import { Inventory, InventoryCategory, InventoryGroup, CreateInventoryRequest } from '../types/inventory';
import { getInventories, getCategories, getGroups, createInventory, updateInventory, deleteInventory } from '../services/inventoryApi';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import CreateEditInventoryModal from '../components/modals/CreateEditInventoryModal';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/auth';

interface PaginatedData {
    current_page: number;
    data: Inventory[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: Array<{ url: string | null; label: string; active: boolean }>;
    next_page_url: string | null;
    path: string;
    per_page: number;
    prev_page_url: string | null;
    to: number;
    total: number;
}

interface ApiResponse {
    success: boolean;
    data: PaginatedData;
}

export default function InventoryPage() {
    const { isAdmin } = useAuth();
    const [inventories, setInventories] = useState<ApiResponse>({
        success: true,
        data: {
            current_page: 1,
            data: [],
            first_page_url: '',
            from: 1,
            last_page: 1,
            last_page_url: '',
            links: [],
            next_page_url: null,
            path: '',
            per_page: 10,
            prev_page_url: null,
            to: 0,
            total: 0
        }
    });
    const [categories, setCategories] = useState<InventoryCategory[]>([]);
    const [groups, setGroups] = useState<InventoryGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [selectedGroup, setSelectedGroup] = useState<string>('all');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState<Inventory | null>(null);
    const [pagination, setPagination] = useState<Omit<PaginatedData, 'data'>>({
        current_page: 1,
        first_page_url: '',
        from: 1,
        last_page: 1,
        last_page_url: '',
        links: [],
        next_page_url: null,
        path: '',
        per_page: 10,
        prev_page_url: null,
        to: 0,
        total: 0
    });
    const [summaryStats, setSummaryStats] = useState({
        totalItems: 0,
        lowStockItems: 0,
        totalValue: 0,
        consumableItems: 0
    });

    useEffect(() => {
        fetchData();
    }, [searchTerm, pagination.current_page]);

    const calculateSummaryStats = (inventoryData: Inventory[]) => {
        const stats = {
            totalItems: inventoryData.length,
            lowStockItems: 0,
            totalValue: 0,
            consumableItems: 0
        };

        inventoryData.forEach(item => {
            stats.totalValue += Number(item.unit_price);
            if (item.is_consummable) {
                stats.consumableItems++;
                if (item.consummable && item.consummable.quantity < 10) {
                    stats.lowStockItems++;
                }
            }
        });

        setSummaryStats(stats);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const [inventoriesRes, categoriesRes, groupsRes] = await Promise.all([
                getInventories(searchTerm, pagination.per_page, pagination.current_page),
                getCategories(),
                getGroups()
            ]);

            const paginatedData = inventoriesRes.data.data;
            setInventories(inventoriesRes.data);
            setPagination({
                current_page: paginatedData.current_page,
                first_page_url: paginatedData.first_page_url,
                from: paginatedData.from,
                last_page: paginatedData.last_page,
                last_page_url: paginatedData.last_page_url,
                links: paginatedData.links,
                next_page_url: paginatedData.next_page_url,
                path: paginatedData.path,
                per_page: paginatedData.per_page,
                prev_page_url: paginatedData.prev_page_url,
                to: paginatedData.to,
                total: paginatedData.total
            });
            setCategories(categoriesRes.data.data);
            setGroups(groupsRes.data.data);
            calculateSummaryStats(paginatedData.data);
        } catch (error) {
            setError('Failed to fetch inventory data');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (inventory: Inventory) => {
        setSelectedInventory(inventory);
        setIsEditModalOpen(true);
    };

    const handleDelete = (inventory: Inventory) => {
        setSelectedInventory(inventory);
        setIsDeleteModalOpen(true);
    };

    const handleCreate = async (data: CreateInventoryRequest) => {
        try {
            await createInventory(data);
            await fetchData();
        } catch (error) {
            throw error;
        }
    };

    const handleUpdate = async (data: CreateInventoryRequest) => {
        if (!selectedInventory) return;
        try {
            await updateInventory(selectedInventory.id, data);
            await fetchData();
        } catch (error) {
            throw error;
        }
    };

    const confirmDelete = async () => {
        if (!selectedInventory) return;
        try {
            await deleteInventory(selectedInventory.id);
            await fetchData();
            setIsDeleteModalOpen(false);
            setSelectedInventory(null);
        } catch (error) {
            setError('Failed to delete inventory item');
            console.error(error);
        }
    };

    const filteredInventories = (inventories.data.data || []).filter((inventory: Inventory) => {
        const matchesCategory = selectedCategory === 'all' ||
            inventory.group?.category?.id.toString() === selectedCategory;
        const matchesGroup = selectedGroup === 'all' ||
            inventory.group?.id.toString() === selectedGroup;
        return matchesCategory && matchesGroup;
    });

    const handlePageChange = (page: number) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    };

    const PaginationControls = () => {
        const start = ((pagination.current_page - 1) * pagination.per_page) + 1;
        const end = Math.min(pagination.current_page * pagination.per_page, pagination.total);

        return (
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => handlePageChange(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => handlePageChange(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.last_page}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{start.toString()}</span> to{' '}
                            <span className="font-medium">{end.toString()}</span>{' '}
                            of <span className="font-medium">{pagination.total.toString()}</span> results
                        </p>
                    </div>
                    <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                            <button
                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                disabled={pagination.current_page === 1}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                            >
                                <span className="sr-only">Previous</span>
                                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                            </button>
                            {[...Array(pagination.last_page)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => handlePageChange(i + 1)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${pagination.current_page === i + 1
                                        ? 'z-10 bg-indigo-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                                        : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                                        }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                disabled={pagination.current_page === pagination.last_page}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                            >
                                <span className="sr-only">Next</span>
                                <ChevronRight className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-gray-500">Loading inventory data...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="mb-6 space-y-4 sm:space-y-0 sm:flex sm:justify-between sm:items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
                    <p className="text-gray-500">Manage inventory items</p>
                </div>
                {isAdmin && (
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Link
                            to="/inventory/manage"
                            className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
                        >
                            <Settings size={20} />
                            Manage Categories
                        </Link>
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                        >
                            <Plus size={20} />
                            Add Item
                        </button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500">Total Items</div>
                    <div className="text-2xl font-bold">{summaryStats.totalItems}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500">Low Stock Items</div>
                    <div className="text-2xl font-bold text-yellow-600">{summaryStats.lowStockItems}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500">Total Value</div>
                    <div className="text-2xl font-bold">R {summaryStats.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <div className="text-sm text-gray-500">Consumable Items</div>
                    <div className="text-2xl font-bold">{summaryStats.consumableItems}</div>
                </div>
            </div>

            {error && (
                <div className="p-4 text-red-700 bg-red-100 rounded-lg">
                    {error}
                </div>
            )}

            <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-1/3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search inventory..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 w-full border rounded-lg"
                        />
                    </div>
                </div>

                <div className="w-full md:w-1/3">
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                    >
                        <option value="all">All Categories</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="w-full md:w-1/3">
                    <select
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                    >
                        <option value="all">All Groups</option>
                        {groups.map(group => (
                            <option key={group.id} value={group.id}>
                                {group.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Category/Group
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    SKU/Serial
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Quantity/Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Price
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Details
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredInventories.map((inventory) => (
                                <tr key={inventory.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <Package className="w-5 h-5 text-gray-400 mr-2" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {inventory.name}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {inventory.description}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {inventory.group?.category?.name}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {inventory.group?.name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {inventory.sku || '-'}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {inventory.serial_number || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {inventory.is_consummable ? (
                                            <div className="flex items-center">
                                                <div className="text-sm text-gray-900">
                                                    Qty: {inventory.consummable?.quantity || 0}
                                                </div>
                                                {inventory.consummable?.quantity && inventory.consummable.quantity < 10 && (
                                                    <AlertTriangle className="w-4 h-4 text-yellow-500 ml-2" />
                                                )}
                                            </div>
                                        ) : (
                                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                Non-Consumable
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                            {inventory.is_consummable ? (
                                                <>
                                                    R {(Number(inventory.unit_price) / (inventory.consummable?.quantity || 1)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per item
                                                </>
                                            ) : (
                                                <>
                                                    R {Number(inventory.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </>
                                            )}
                                        </div>
                                        {inventory.is_consummable && (
                                            <div className="text-sm text-gray-500">
                                                Total: R {Number(inventory.unit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500">
                                            {inventory.is_consummable && inventory.consummable && (
                                                <>
                                                    <div>Part: {inventory.consummable.part_name || '-'}</div>
                                                    <div>Part #: {inventory.consummable.part_number || '-'}</div>
                                                </>
                                            )}
                                            <div>PO: {inventory.po_number || '-'}</div>
                                            <div>Invoice: {inventory.invoice_number || '-'}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        {isAdmin && (
                                            <>
                                                <button
                                                    onClick={() => handleEdit(inventory)}
                                                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(inventory)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <PaginationControls />
            </div>

            <CreateEditInventoryModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreate}
            />

            <CreateEditInventoryModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedInventory(null);
                }}
                onSubmit={handleUpdate}
                inventory={selectedInventory || undefined}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedInventory(null);
                }}
                onConfirm={confirmDelete}
                itemType="Inventory Item"
                itemName={selectedInventory?.name || ''}
            />
        </div>
    );
} 